import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { Bell, Plus, X, Save, Mail, Clock } from "lucide-react";

const ALERT_TYPES = [
  { key: "zero_activity", label: "Zero Activity Alert", desc: "Alert when no entries are logged during active shift", fields: [{ key: "threshold_hours", label: "Inactivity Threshold (hours)", type: "number", step: 1, min: 1 }, { key: "check_interval_minutes", label: "Check Interval (minutes)", type: "number", step: 5, min: 5 }] },
  { key: "visitor_overstay", label: "Visitor Overstay", desc: "Alert when visitors exceed allowed time on premises", fields: [{ key: "threshold_hours", label: "Overstay Threshold (hours)", type: "number", step: 1, min: 1 }, { key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "overdue_rgp", label: "Overdue RGP", desc: "Alert when returnable gate pass items are not returned", fields: [{ key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "key_not_returned", label: "Key Not Returned", desc: "Alert when keys are not returned after shift", fields: [{ key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "jobwork_overdue", label: "Job Work Overdue", desc: "Alert when job work items are pending beyond threshold", fields: [{ key: "threshold_days", label: "Overdue Threshold (days)", type: "number", step: 1, min: 1 }, { key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "daily_digest", label: "Daily Digest", desc: "Daily summary of gate activity sent at 8 PM IST", fields: [] },
  { key: "daily_backup", label: "Daily Data Backup", desc: "CSV backup of all modules sent at 11 PM IST", fields: [] },
  { key: "vehicle_service_due", label: "Vehicle Service Due", desc: "Alert before a vehicle's next scheduled service", fields: [{ key: "lead_days", label: "Notify Before (days)", type: "number", step: 1, min: 1 }, { key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "vehicle_insurance_due", label: "Vehicle Insurance Expiry", desc: "Alert before vehicle insurance expiry", fields: [{ key: "lead_days", label: "Notify Before (days)", type: "number", step: 1, min: 1 }, { key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "vehicle_fitness_due", label: "Vehicle Fitness / Passing Expiry", desc: "Alert before RTO fitness / passing expiry", fields: [{ key: "lead_days", label: "Notify Before (days)", type: "number", step: 1, min: 1 }, { key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
  { key: "vehicle_puc_due", label: "Vehicle PUC Expiry", desc: "Alert before PUC certificate expiry", fields: [{ key: "lead_days", label: "Notify Before (days)", type: "number", step: 1, min: 1 }, { key: "cooldown_hours", label: "Re-alert Cooldown (hours)", type: "number", step: 1, min: 1 }] },
];

export default function AlertConfigTab() {
  const [config, setConfig] = useState({ alerts: {}, global_recipients: [] });
  const [saving, setSaving] = useState(false);
  const [newGlobalEmail, setNewGlobalEmail] = useState("");
  const [newAlertEmails, setNewAlertEmails] = useState({});

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/alert-recipients");
      setConfig({
        alerts: data.alerts || {},
        global_recipients: data.global_recipients || [],
      });
    } catch (err) {
      toast.error("Failed to load alert config");
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const updateAlert = (alertKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      alerts: {
        ...prev.alerts,
        [alertKey]: { ...(prev.alerts[alertKey] || {}), [field]: value },
      },
    }));
  };

  const addAlertEmail = (alertKey) => {
    const email = (newAlertEmails[alertKey] || "").trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    const current = config.alerts[alertKey]?.emails || [];
    if (current.includes(email)) { toast.error("Already added"); return; }
    updateAlert(alertKey, "emails", [...current, email]);
    setNewAlertEmails(prev => ({ ...prev, [alertKey]: "" }));
  };

  const removeAlertEmail = (alertKey, email) => {
    const current = config.alerts[alertKey]?.emails || [];
    updateAlert(alertKey, "emails", current.filter(e => e !== email));
  };

  const addGlobalEmail = () => {
    const email = newGlobalEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (config.global_recipients.includes(email)) { toast.error("Already added"); return; }
    setConfig(prev => ({ ...prev, global_recipients: [...prev.global_recipients, email] }));
    setNewGlobalEmail("");
  };

  const removeGlobalEmail = (email) => {
    setConfig(prev => ({ ...prev, global_recipients: prev.global_recipients.filter(e => e !== email) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/alert-recipients", config);
      toast.success("Alert configuration saved");
    } catch (err) {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      {/* Global Fallback Recipients */}
      <Card className="p-4 sm:p-5 border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-sm tracking-wider uppercase text-gray-700 flex items-center gap-2">
              <Mail size={16} className="text-[#1B3C87]" /> Global Fallback Recipients
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">Used when an alert type has no specific recipients configured</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {config.global_recipients.map(e => (
            <span key={e} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-xs font-medium text-gray-700">
              {e} <button onClick={() => removeGlobalEmail(e)} className="hover:text-red-600"><X size={12} /></button>
            </span>
          ))}
          {config.global_recipients.length === 0 && <span className="text-xs text-gray-400 italic">No global recipients configured. Alerts will not be sent unless per-alert recipients are set.</span>}
        </div>
        <div className="flex gap-2">
          <Input value={newGlobalEmail} onChange={e => setNewGlobalEmail(e.target.value)} placeholder="email@example.com" className="h-9 text-sm max-w-xs" onKeyDown={e => e.key === "Enter" && addGlobalEmail()} data-testid="global-email-input" />
          <Button size="sm" variant="outline" onClick={addGlobalEmail} className="h-9" data-testid="add-global-email-btn"><Plus size={14} className="mr-1" /> Add</Button>
        </div>
      </Card>

      {/* Per-Alert Configuration */}
      {ALERT_TYPES.map(at => {
        const alertCfg = config.alerts[at.key] || {};
        const isEnabled = alertCfg.enabled !== false;
        const emails = alertCfg.emails || [];

        return (
          <Card key={at.key} className={`p-4 sm:p-5 transition-all ${isEnabled ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`alert-card-${at.key}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-sm tracking-wider uppercase text-gray-700 flex items-center gap-2">
                  <Bell size={14} className={isEnabled ? "text-emerald-600" : "text-gray-400"} /> {at.label}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">{at.desc}</p>
              </div>
              <Switch checked={isEnabled} onCheckedChange={v => updateAlert(at.key, "enabled", v)} data-testid={`toggle-${at.key}`} />
            </div>

            {isEnabled && (
              <div className="space-y-3">
                {/* Threshold/Duration Fields */}
                {at.fields.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {at.fields.map(f => (
                      <div key={f.key} className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 flex items-center gap-1">
                          <Clock size={10} /> {f.label}
                        </Label>
                        <Input
                          type={f.type} step={f.step} min={f.min}
                          value={alertCfg[f.key] ?? ""}
                          onChange={e => updateAlert(at.key, f.key, parseFloat(e.target.value) || 0)}
                          className="h-9 text-sm"
                          data-testid={`field-${at.key}-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Per-alert Email Recipients */}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-400 flex items-center gap-1 mb-1.5">
                    <Mail size={10} /> Recipients {emails.length === 0 && "(using global fallback)"}
                  </Label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {emails.map(e => (
                      <span key={e} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-[11px] font-medium text-blue-700">
                        {e} <button onClick={() => removeAlertEmail(at.key, e)} className="hover:text-red-600"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newAlertEmails[at.key] || ""} onChange={e => setNewAlertEmails(p => ({ ...p, [at.key]: e.target.value }))}
                      placeholder="Add email for this alert" className="h-8 text-xs max-w-xs"
                      onKeyDown={e => e.key === "Enter" && addAlertEmail(at.key)}
                      data-testid={`email-input-${at.key}`}
                    />
                    <Button size="sm" variant="ghost" onClick={() => addAlertEmail(at.key)} className="h-8 text-xs"><Plus size={12} /></Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11 px-8" data-testid="save-alert-config-btn">
          <Save size={16} className="mr-1" /> {saving ? "Saving..." : "Save Alert Configuration"}
        </Button>
      </div>
    </div>
  );
}
